import { useState } from "react";
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { ChevronDownIcon, BookOpenIcon, MapIcon, ClipboardListIcon, LightBulbIcon, QuestionMarkCircleIcon, SupportIcon } from '@heroicons/react/solid';
const tocItems = [
  { href: "#overview", label: "Overview", icon: BookOpenIcon },
  { href: "#navigation-guide", label: "Navigation Guide", icon: MapIcon },
  { href: "#usage-instructions", label: "Usage Instructions", icon: ClipboardListIcon },
  { href: "#tips-and-best-practices", label: "Tips & Best Practices", icon: LightBulbIcon },
  { href: "#faqs", label: "FAQs", icon: QuestionMarkCircleIcon },
  { href: "#contact-support", label: "Contact Support", icon: SupportIcon }
];

const faqs = {
  "General Questions": [
    {
      q: "What is the Leadership Simulator?",
      a: "A tool to help leaders practice communication skills through realistic, AI-driven scenarios.",
    },
    {
      q: "What browsers are supported?",
      a: "Best used on Chrome, Edge, or Firefox. Keep your browser updated for optimal experience.",
    },
    {
      q: "Do I need a microphone to use the simulator?",
      a: "Recommended, but you can also type responses if needed.",
    },
    {
      q: "How do I enable my microphone for the simulator?",
      a: "Click 'Allow' when prompted or adjust site permissions in your browser settings.",
    },
  ],
  "Using the Simulator": [
    {
      q: "Can I save my progress?",
      a: "Yes, your scenarios and conversations are saved to your account.",
    },
    {
      q: "How do I create a custom scenario?",
      a: "Go to the Design Scenario page, complete the form, and click 'Save and Start.'",
    },
    {
      q: "What happens if I lose connection during a simulation?",
      a: "The simulation will pause and resume once you're reconnected.",
    },
    {
      q: "Can I continue a conversation I started earlier?",
      a: "Yes, continuing a conversation creates a new version with saved history.",
    },
  ],
  "Feedback and Insights": [
    {
      q: "How is the feedback generated?",
      a: "Based on tone, phrasing, and content. It assesses autonomy, competence, and relatedness.",
    },
    {
      q: "Can I view feedback from previous simulations?",
      a: "Yes, use the Feedback and History page for full access.",
    },
    {
      q: "How can I improve my feedback scores?",
      a: "Focus on empathy, clarity, and actionable conversation steps.",
    },
  ],
  "Technical Support": [
    {
      q: "What should I do if the simulator isn't working?",
      a: "Refresh the page, clear your cache, or try another browser. If needed, email support@intrinsicfirst.com.",
    },
    {
      q: "How do I enable microphone access after denying it?",
      a: "Open browser settings, update site permissions to allow microphone, then refresh the page.",
    },
    {
      q: "How can I reset my password?",
      a: "Click 'Forgot Password' on the login page and follow the instructions.",
    },
    {
      q: "How do I report a bug or provide feedback about the simulator?",
      a: "Email support@intrinsicfirst.com.",
    },
  ],
};

const CollapsibleSection = ({ title, children }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="border border-gray-200 rounded-lg shadow-sm mb-6">
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left px-5 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 rounded-t-lg transition"
      >
        <span className="font-semibold text-lg text-gray-800">{title}</span>
        <svg
          className={`w-5 h-5 transform transition-transform ${open ? "rotate-180" : ""}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="px-6 py-4 bg-white border-t border-gray-200">{children}</div>
      )}
    </div>
  );
};

const HelpPage = () => {
  return (
    <div className="px-6 py-8 max-w-6xl mx-auto font-sans text-gray-800 shadow-lg">
      <div className="bg-white shadow-xl rounded-lg px-8 py-10 max-w-6xl mx-auto mb-8">
        <div className="text-center">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-4 tracking-tight">ProACTr Help Center</h1>
          <p className="text-lg text-gray-600 mb-2">
            Welcome to the ProACTr Help Center – your guide to mastering difficult conversations through AI-powered simulation.
          </p>
          <p className="text-md text-gray-500">
            Whether you're preparing for high-stakes feedback or building leadership confidence, this tool gives you a safe, supportive space to practice, learn, and grow.
          </p>
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg p-6 mb-10">
        <h2 className="text-2xl font-semibold mb-3">Key Features of the Leadership Simulator</h2>
        <ul className="list-disc pl-5 mb-8 space-y-2">
          <li>
            <strong>AI-Driven Conversations:</strong>
            <ul className="list-disc pl-5 mt-1 space-y-1">
              <li>Interact with realistic, AI-powered employees who respond to your tone, phrasing, and content.</li>
              <li>Practice handling performance issues, giving feedback, or building trust in a professional setting.</li>
            </ul>
          </li>
          <li>
            <strong>Prewritten Scenarios:</strong>
            <ul className="list-disc pl-5 mt-1 space-y-1">
              <li>Choose from a library of carefully crafted scenarios, each designed to simulate common workplace challenges.</li>
              <li>Scenarios are available in multiple difficulty levels to match your experience.</li>
            </ul>
          </li>
          <li>
            <strong>Custom Scenario Creation:</strong>
            <ul className="list-disc pl-5 mt-1 space-y-1">
              <li>Create your own scenarios by defining the situation, employee profile, and performance issues.</li>
              <li>Tailor simulations to meet your unique leadership development needs.</li>
            </ul>
          </li>
          <li>
            <strong>Feedback and Insights:</strong>
            <ul className="list-disc pl-5 mt-1 space-y-1">
              <li>Receive detailed feedback after each simulation, including analysis of your communication style and its impact on the employee’s sense of autonomy, competence, and relatedness.</li>
              <li>Use insights to identify strengths and areas for improvement.</li>
            </ul>
          </li>
          <li>
            <strong>Speech-to-Text and Text-to-Speech Integration:</strong>
            <ul className="list-disc pl-5 mt-1 space-y-1">
              <li>Communicate with the simulator using your voice or text input.</li>
              <li>Listen to the AI’s responses in lifelike, conversational tones.</li>
            </ul>
          </li>
        </ul>
      </div>

      {/* Table of Contents */}
      <div className="bg-white shadow-md rounded-lg p-6 mb-10">
        <h2 className="text-2xl font-semibold mb-4">Table of Contents</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {tocItems.map(({ href, label, icon: Icon }) => (
            <a
              key={href}
              href={href}
              className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition duration-150"
            >
              <Icon className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-800">{label}</span>
            </a>
          ))}
        </div>
      </div>


      <section id="overview" className="mt-10">
        <div className="bg-white shadow-lg rounded-xl p-8 transition-all duration-300">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Overview</h2>
          <p className="text-gray-700 mb-4">
            The Leadership Simulator is a cutting-edge tool designed to help leaders practice and refine
            their communication skills in a simulated, AI-driven environment.
          </p>
          <p className="text-gray-700 mb-6">Key features include:</p>
          <ul className="space-y-4">
            {[
              { title: "AI-Driven Conversations", desc: "Interact with realistic, AI-powered employees." },
              { title: "Prewritten Scenarios", desc: "Choose from a library of crafted scenarios." },
              { title: "Custom Scenario Creation", desc: "Define your own scenarios for practice." },
              { title: "Feedback and Insights", desc: "Receive detailed feedback on your performance." },
              { title: "Speech-to-Text and Text-to-Speech", desc: "Communicate with the simulator naturally." },
            ].map((feature, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <div className="mt-1 w-3 h-3 rounded-full bg-blue-600 flex-shrink-0"></div>
                <div>
                  <p className="font-semibold text-gray-800">{feature.title}</p>
                  <p className="text-gray-600 text-sm">{feature.desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section id="navigation-guide" className="mt-10">
        <div className="bg-white shadow-lg rounded-xl p-8 transition-all duration-300">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Navigation Guide</h2>
          <p className="text-gray-700 mb-6">
            The Leadership Simulator is organized into several key pages to help you navigate and use the tool effectively.
            Here’s a quick overview of the main sections:
          </p>

          <div className="divide-y divide-gray-200">
            {[
              {
                title: "Dashboard",
                desc: "The starting point of the simulator. Access prewritten scenarios, create custom scenarios, or view your progress.",
              },
              {
                title: "Explore Scenarios Page",
                desc: "Choose from a categorized list of prewritten scenarios by difficulty to simulate workplace challenges.",
              },
              {
                title: "Create New Scenario Page",
                desc: "Tailor your own scenarios by defining the situation, employee profile, and leadership goals.",
              },
              {
                title: "Conversation Simulator Page",
                desc: "The heart of the tool — engage in conversations with AI-powered employees via text or speech. Gauges track employee emotions in real-time.",
              },
              {
                title: "My Scenarios Page",
                desc: "Manage your custom scenarios. View, edit, simulate, or delete past creations.",
              },
              {
                title: "Conversation History & Reports Page",
                desc: "Review all conversations and reports. Gain insights on motivation, leadership skills, and emotional intelligence.",
              },
              {
                title: "Profile Page",
                desc: "Manage your profile and preferences to personalize your simulation experience.",
              },
            ].map((item, idx) => (
              <div key={idx} className="py-4">
                <p className="font-semibold text-gray-900 text-lg">{item.title}</p>
                <p className="text-gray-600 text-sm mt-1">{item.desc}</p>
              </div>
            ))}
          </div>

          <p className="text-gray-700 mt-6">
            Use the navigation links at the top of the page (or the dropdown menu on smaller screens) to switch between these sections.
          </p>
        </div>
      </section>

      {/* Usage Instructions Section */}
      <section id="usage-instructions" className="mt-10 bg-white shadow-md rounded-lg p-6">
        <h2 className="text-3xl font-bold text-gray-800 mb-6">Usage Instructions</h2>
        <p className="text-gray-600 mb-8">
          This guide provides an overview of the key pages in the Leadership Simulator and explains how to navigate and use them effectively.
        </p>

        {/* Home Page */}
        <CollapsibleSection title="Dashboard Overview">
          <h3 className="text-xl font-semibold mb-2">Dashboard Overview</h3>
          <p className="mb-4">
            The Dashboard is your personalized command center. It gives you quick access to your recent activity, progress tracking,
            skill insights, and recommended next steps — all in one modern, streamlined layout.
          </p>
          <ul className="list-disc pl-5 space-y-2 mb-6">
            <li>
              <strong>Progress Widget:</strong> See your current leadership growth status including XP points, completed simulations, and level progress.
            </li>
            <li>
              <strong>Custom Scenarios Widget:</strong> Quickly access, edit, or launch your own scenarios from a curated list of your custom content.
            </li>
            <li>
              <strong>Streak Tracker:</strong> Visualize your practice streaks to keep your leadership skills sharp through regular simulation use.
            </li>
            <li>
              <strong>Analytics Snapshot:</strong> View high-level data on your most recent leadership ratings and skill development trends.
            </li>
            <li>
              <strong>Quick Actions:</strong> Jump directly into key tasks like starting a new simulation, creating a scenario, or continuing a past session.
            </li>
            <li>
              <strong>Skill Breakdown:</strong> Analyze your feedback reports by skill category to track strengths and areas for improvement.
            </li>
            <li>
              <strong>Suggested Scenario:</strong> AI-recommended scenario tailored to your recent trends and skill gaps.
            </li>
            <li>
              <strong>Recent Conversations:</strong> Instantly continue or review the latest simulations you've had with AI employees.
            </li>
            <li>
              <strong>Feedback Highlights:</strong> See quick takeaways and standout feedback from your most recent conversations.
            </li>
          </ul>
          <p className="text-gray-700">
            You can return to your dashboard anytime via the navigation bar to track your journey and launch into action.
          </p>
        </CollapsibleSection>


        {/* Select Scenario Page */}
        <CollapsibleSection title="Scenario Selection Page">
          <h3 className="text-xl font-semibold mb-2">Scenario Selection Page</h3>
          <p className="mb-4">
            The Scenario Selection Page allows you to browse and explore a wide variety of prewritten scenarios. These scenarios are designed to reflect real-world leadership challenges and are categorized by topic and difficulty. You can apply filters, search by keyword, and preview scenario details.
          </p>

          <ul className="list-disc pl-5 space-y-2 mb-4">
            <li>
              <strong>Search:</strong> Use the search bar to find scenarios by keywords, employee names, or subjects. This helps narrow results based on scenario content or titles.
            </li>
            <li>
              <strong>Filter Options:</strong> Three types of filters help you customize your view:
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li><strong>Type Filters:</strong> Choose from "All", "Featured", "New", or "Most Popular".</li>
                <li><strong>Difficulty Filter:</strong> Select Beginner, Intermediate, or Advanced scenarios.</li>
                <li><strong>Category Filter:</strong> Choose from topics like Motivation, Communication, Adaptability, Boundaries, and more using the side menu.</li>
              </ul>
            </li>
            <li>
              <strong>Scenario Cards:</strong> Each card includes:
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>The scenario title and summary</li>
                <li>Scenario tags, categories, and difficulty level</li>
                <li>A special badge if it's "Featured", "New", or "Most Popular"</li>
                <li>A call-to-action button to start or a prompt to upgrade (for free users)</li>
              </ul>
            </li>
            <li>
              <strong>Access Control:</strong> Free users are limited to a specific set of scenarios. If a scenario is locked, the "Upgrade for Access" button appears instead of "Start Scenario". Paid users have full access to the entire scenario library.
            </li>
          </ul>

          <p>
            The intuitive layout and detailed filtering system make it easy to explore scenarios tailored to your leadership development goals.
          </p>
        </CollapsibleSection>

        {/* Design Scenario Page */}
        <CollapsibleSection title="Create a Custom Scenario">
          <h3 className="text-xl font-semibold mb-3">Overview</h3>
          <p className="mb-4">
            The Custom Scenario Form allows you to design your own simulated leadership conversations. This tailored experience is ideal for practicing real-world scenarios by inputting specific details about your employee, workplace dynamics, and the challenges you want to address. The form is divided into multiple key sections:
          </p>

          <h4 className="text-lg font-semibold mb-2">1. Scenario Title</h4>
          <p className="mb-4">
            Start by giving your scenario a descriptive title. This title will help you identify it later in the "My Scenarios" page.
          </p>

          <h4 className="text-lg font-semibold mb-2">2. Employee & Job Details</h4>
          <ul className="list-disc pl-5 space-y-2 mb-4">
            <li><strong>Name & Age:</strong> Input the employee's name and estimated age.</li>
            <li><strong>Job Title:</strong> Specify their position within the organization.</li>
            <li><strong>Experience:</strong> Indicate tenure at the company, time in current job, and total experience.</li>
            <li><strong>Voice & Profile Picture:</strong> Choose a voice (male/female) and optionally a profile picture.</li>
            <li><strong>Job Responsibilities:</strong> Describe the employee’s core duties.</li>
          </ul>

          <h4 className="text-lg font-semibold mb-2">3. Scenario Description</h4>
          <ul className="list-disc pl-5 space-y-2 mb-4">
            <li><strong>Summary:</strong> Write a short overview of the situation or issue.</li>
            <li><strong>Performance Issues:</strong> Select applicable behaviors (e.g., missed deadlines, poor communication).</li>
            <li><strong>Interpersonal Issues:</strong> Identify relevant social or team-based challenges.</li>
            <li><strong>Prior Discussions:</strong> Indicate whether this topic has been discussed with the employee previously.</li>
            <li><strong>Specific Examples:</strong> Provide examples yourself or use the checkbox to let AI generate them.</li>
            <li><strong>Additional Context:</strong> Mention external influences like high workload, organizational change, etc.</li>
          </ul>

          <h4 className="text-lg font-semibold mb-2">4. Employee Background</h4>
          <ul className="list-disc pl-5 space-y-2 mb-4">
            <li><strong>Understanding:</strong> Rate the employee’s awareness of the issue.</li>
            <li><strong>Team Relationships:</strong> Describe how well they collaborate with others.</li>
            <li><strong>Feelings:</strong> Select emotional responses (e.g., confused, resentful). If “Other” is selected, a text box appears.</li>
            <li><strong>Personal Factors:</strong> Choose any relevant personal stressors (e.g., health, finances).</li>
            <li><strong>Other Details:</strong> Add any additional notes about the employee’s behavior or background.</li>
          </ul>

          <h4 className="text-lg font-semibold mb-2">5. Interpersonal Profile Factors</h4>
          <p className="mb-2">
            These fields define the relationship and emotional context between you and the employee:
          </p>
          <ul className="list-disc pl-5 space-y-2 mb-4">
            <li><strong>Familiarity:</strong> Low, Medium, or High — how well you know the employee.</li>
            <li><strong>Emotion:</strong> Indicates how emotionally expressive the employee is.</li>
            <li><strong>Trust:</strong> Choose levels for both Logical Trust and Emotional Trust to build a composite trust score.</li>
          </ul>
          <p className="mb-4 italic text-sm text-yellow-700">
            Free plan users are locked into "medium" settings for trust, familiarity, and emotion.
          </p>

          <h4 className="text-lg font-semibold mb-2">6. Personality Profile (Disposition)</h4>
          <p className="mb-2">
            Adjust sliders across 9 continua (e.g., Engage vs Withdraw, Optimist vs Pessimist). These affect the simulated employee’s behavior.
          </p>
          <p className="mb-2 italic text-sm text-yellow-700">
            Free users can only adjust the first 3 continua.
          </p>
          <ul className="list-disc pl-5 space-y-2 mb-4">
            <li>Each slider includes tooltips to explain its impact and description.</li>
            <li>Values selected here help personalize the simulation’s depth and response nuance.</li>
          </ul>

          <h4 className="text-lg font-semibold mb-2">7. Optional: Calculate DISC Profile</h4>
          <p className="mb-4">
            Use the "Calculate DISC Type" button to auto-generate a DISC profile based on your slider settings. You’ll see the profile and a short description.
          </p>

          <h4 className="text-lg font-semibold mb-2">8. Submit Your Scenario</h4>
          <ul className="list-disc pl-5 space-y-2 mb-4">
            <li><strong>Create:</strong> Saves a new scenario to your account.</li>
            <li><strong>Update:</strong> Available if you're editing an existing scenario.</li>
            <li><strong>Limits:</strong> Free users can create up to 3 scenarios.</li>
          </ul>
        </CollapsibleSection>


        {/* Simulator Page */}
        <CollapsibleSection title="Conversation Simulator Page">
          <p className="mb-4">
            The Conversation Simulator Page is the heart of the Leadership Simulator. It’s where you engage in dynamic, real-time conversations with an AI-powered employee. You must enter this page by selecting a scenario—either prewritten, custom, or a continuation of a previous conversation. Once inside, you can use voice or text to communicate, track the impact of your communication, and analyze performance.
          </p>

          <ul className="list-disc pl-5 space-y-2 mb-6">
            <li>
              <strong>Conversation Panel:</strong> On the left side, this panel contains the message history, voice and text input, and conversation controls. You can type your message directly or click the microphone button to use speech-to-text. When the mic turns red, begin speaking; the message will transcribe live and send when you're ready.
            </li>
            <li>
              <strong>Realtime Gauges:</strong> A standout feature. Located above the message box, these dials update after each message to show the simulated employee's emotional charge and motivational state (autonomy, competence, and relatedness). Use them to adapt your strategy on the fly. You can toggle their visibility.
            </li>
            <li>
              <strong>Enable Employee Audio:</strong> Toggle this option to hear the AI employee’s responses read aloud in natural, conversational tone. It’s an immersive option that enhances realism.
            </li>
            <li>
              <strong>Scenario Profile Panel:</strong> On the right side of the screen, this shows the simulated employee’s details—name, job role, profile picture, personality traits (with slider visuals), scenario description, and example issues. This context helps guide your communication choices.
            </li>
            <li>
              <strong>Restart Tutorial:</strong> Use this button to relaunch the guided tutorial for an interactive walkthrough of the simulator’s features.
            </li>
            <li>
              <strong>Finish & Analyze:</strong> When the conversation is complete, click this button to generate a detailed feedback report. It evaluates leadership style, micro and macro skills, emotional intelligence, and motivation impact. Reports are viewable later in the Conversations & Reports section.
            </li>
            <li>
              <strong>Restart Button:</strong> Save your current conversation and start a fresh instance of the same scenario. This is useful for trying different leadership approaches.
            </li>
            <li>
              <strong>Responsive Design:</strong> The page dynamically adapts for resizing. You can even drag the center divider to adjust left and right panel width.
            </li>
            <li>
              <strong>Advanced Tutorial System:</strong> A guided walkthrough appears on first use. It steps through scenario details, message box, audio toggles, gauges, and more—ensuring every user is onboarded smoothly.
            </li>
          </ul>

          <p>
            Whether you're practicing tone, adapting to challenging profiles, or learning how emotional intelligence plays out in real-time conversation, the Simulator Page provides a rich, interactive environment to sharpen your skills.
          </p>
        </CollapsibleSection>

        {/* My Scenarios Page */}
        <CollapsibleSection title="My Custom Scenarios Page">
          <h3 className="text-xl font-semibold mb-3">Manage Your Created Scenarios</h3>
          <p className="mb-4">
            The My Custom Scenarios Page allows you to view, edit, use, and delete any custom scenarios you've created. The page is divided into two main panels:
          </p>

          <ul className="list-disc pl-5 space-y-3 mb-6">
            <li>
              <strong>Left Panel - Scenario List:</strong> This section lists all of your saved custom scenarios, sorted by the most recently created at the top. Each scenario card includes:
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li><strong>Title:</strong> The custom name of the scenario.</li>
                <li><strong>Creation Date:</strong> Formatted for easy reading.</li>
                <li><strong>Summary:</strong> A brief description from your scenario input.</li>
                <li><strong>Actions:</strong> You can perform the following:
                  <ul className="list-disc pl-5 mt-1">
                    <li><strong>View:</strong> Display full details in the right panel.</li>
                    <li><strong>Edit:</strong> Load the scenario into the creation form. (Disabled on the Free Plan with an upgrade prompt.)</li>
                    <li><strong>Use:</strong> Launch the simulator using the selected scenario.</li>
                    <li><strong>Delete:</strong> Permanently remove the scenario (with confirmation modal).</li>
                  </ul>
                </li>
              </ul>
            </li>

            <li>
              <strong>Right Panel - Scenario Details:</strong> When a scenario is selected, its full details are shown in a neatly organized layout including:
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li><strong>Title, Date, and Difficulty:</strong> Displayed at the top for reference.</li>
                <li><strong>Description and Examples:</strong> Review the scenario content and talking points.</li>
                <li><strong>Profile Information:</strong> Comprehensive display of the personality profile assigned to the employee, including:
                  <ul className="list-disc pl-5 mt-1">
                    <li>Familiarity, Emotion, Trust, Values</li>
                    <li>Slider traits such as Engage/Withdraw, Risk, Optimism, etc.</li>
                  </ul>
                </li>
              </ul>
            </li>

            <li>
              <strong>Confirmation Modal:</strong> Triggered when clicking the Delete button, this modal ensures you don't delete a scenario accidentally. It includes Cancel and Delete options.
            </li>

            <li>
              <strong>Free Plan Limitations:</strong> If you're using the Free Plan, editing scenarios is disabled with an upgrade prompt shown directly on the button.
            </li>
          </ul>

          <p>
            This page is your central hub for managing all the custom content you’ve created. Use it to refine scenarios, return to previous work, or launch simulations based on real-life leadership situations.
          </p>
        </CollapsibleSection>


        <CollapsibleSection title="Conversation History & Reports Page">
          <h3 className="text-xl font-semibold mb-2">Overview</h3>
          <p className="mb-4">
            The Conversations & Reports Page allows you to view, manage, and analyze all of your past conversation simulations.
            Each entry corresponds to a completed or in-progress session with an AI employee. You can view the conversation transcript,
            analyze feedback (if available), continue the dialogue, or delete a conversation.
          </p>

          <h3 className="text-lg font-semibold mb-2">Conversation List (Left Panel)</h3>
          <ul className="list-disc pl-5 space-y-2 mb-6">
            <li>Displays a chronological list of your simulations (most recent first).</li>
            <li>Each card includes a title (derived from the scenario summary), the creation date, and four buttons:
              <ul className="list-disc pl-5 mt-1 space-y-1">
                <li><strong>View Conversation:</strong> See the full conversation transcript.</li>
                <li><strong>Report:</strong> View detailed AI feedback (only visible if feedback was generated).</li>
                <li><strong>Continue Conversation:</strong> Resume the conversation, creating a new thread for follow-up dialogue.</li>
                <li><strong>Delete:</strong> Permanently removes the conversation and report after confirmation.</li>
              </ul>
            </li>
            <li>Each button is styled uniquely and highlights when selected.</li>
            <li>If a report is unavailable, the Report button is disabled and grayed out.</li>
          </ul>

          <h3 className="text-lg font-semibold mb-2">Conversation or Report Viewer (Right Panel)</h3>
          <ul className="list-disc pl-5 space-y-2 mb-6">
            <li><strong>Conversation View:</strong> Shows all messages exchanged between you and the AI employee. Each message is labeled clearly by speaker and rendered with clean formatting.</li>
            <li><strong>Report View:</strong> If feedback was generated, a rich feedback report is displayed with:
              <ul className="list-disc pl-5 mt-1 space-y-1">
                <li>Header, scenario summary, and report date.</li>
                <li>Four major feedback categories (Motivation, Leadership Micro-Skills, Leadership Macro-Skills, Emotional Intelligence).</li>
                <li>Each category contains three sub-ratings.</li>
                <li>Click to expand any sub-rating and see the full background context and AI-generated analysis narrative.</li>
                <li>Each rating is also visualized with a row of purple and white dot markers for quick comparison.</li>
              </ul>
            </li>
          </ul>

          <h3 className="text-lg font-semibold mb-2">Delete Confirmation Modal</h3>
          <ul className="list-disc pl-5 space-y-2 mb-6">
            <li>When the Delete button is clicked, a confirmation modal appears, ensuring accidental deletions are avoided.</li>
            <li>This modal overlays the screen and includes options to confirm or cancel the deletion.</li>
          </ul>

          <p className="mt-4 text-gray-700">
            This page is a critical tool for reviewing your growth and understanding the dynamics of your conversations. Use it regularly to explore different approaches, track performance, and gain insight from feedback reports.
          </p>
        </CollapsibleSection>

        {/* Profile Page */}
        <CollapsibleSection title="Profile Page">
          <h3 className="text-xl font-semibold mb-3">Manage Your Profile</h3>
          <p className="mb-4">
            The Profile Page allows you to view and update your personal information and leadership disposition preferences. These settings personalize your experience within the simulator.
          </p>

          <h4 className="text-lg font-semibold mb-2">Basic Information</h4>
          <ul className="list-disc pl-5 space-y-2 mb-6">
            <li><strong>First Name / Last Name:</strong> Displayed across the app and in reports.</li>
            <li><strong>Job Title / Organization:</strong> Contextual info used in simulations and reports.</li>
          </ul>

          <h4 className="text-lg font-semibold mb-2">Disposition Traits</h4>
          <p className="mb-3">
            Check the <strong>Enable Disposition Traits</strong> box to unlock powerful personality customization through 9 interactive sliders. Each slider represents a continuum of leadership-relevant behavior, allowing you to align simulations with your real-world style.
          </p>
          <ul className="list-disc pl-5 space-y-2 mb-6">
            <li><strong>Engage vs. Withdraw (EW):</strong> Your approach to confrontation—assertive vs. reserved.</li>
            <li><strong>Accept vs. Control (AC):</strong> Flexibility versus control in shaping your environment.</li>
            <li><strong>Extrovert vs. Introvert (EI):</strong> Preference for interaction vs. introspection.</li>
            <li><strong>Direct vs. Indirect (DI):</strong> How transparently you communicate your thoughts.</li>
            <li><strong>Perceive vs. Judge (PJ):</strong> Openness and adaptability vs. decisiveness and structure.</li>
            <li><strong>Risk-Take vs. Risk-Assess (RT):</strong> Boldness versus caution in decision-making.</li>
            <li><strong>Optimist vs. Pessimist (OP):</strong> Your general outlook in planning and expectations.</li>
            <li><strong>Change vs. Continuity (CO):</strong> Preference for novelty versus stability and routine.</li>
            <li><strong>Task vs. People (TP):</strong> Balance between achieving results and fostering relationships.</li>
          </ul>

          <p className="mb-3">
            Each trait slider has 5 settings with dynamic color indicators and tooltips that describe the leadership behavior it represents. Adjust the sliders to reflect your authentic leadership profile. These settings are integrated into your simulations to tailor feedback and response styles.
          </p>

          <p className="mb-6">
            Don’t forget to click <strong>Save Changes</strong> at the bottom of the page to update your preferences.
          </p>
        </CollapsibleSection>
      </section>
      
      {/* Tips and Best Practices Section */}
      <section id="tips-and-best-practices" className="mt-10">
        <div className="bg-white shadow-lg rounded-xl p-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">Tips and Best Practices</h2>
          <p className="text-gray-700 mb-8">
            To get the most out of the Leadership Simulator, follow these expert tips and best practices:
          </p>

          {/* Tips for Effective Use */}
          <h3 className="text-2xl font-semibold text-blue-700 mb-4">Tips for Effective Use</h3>
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {[
              {
                title: "Speak Naturally",
                desc: "Speak clearly with the mic for better transcription and realistic AI replies.",
              },
              {
                title: "Match Difficulty to Experience",
                desc: "Start with beginner scenarios and progress to more complex ones.",
              },
              {
                title: "Focus on Feedback",
                desc: "Carefully review simulation feedback to uncover growth areas.",
              },
              {
                title: "Explore Multiple Scenarios",
                desc: "Broaden your leadership range by tackling diverse simulations.",
              },
              {
                title: "Experiment with Styles",
                desc: "Try different tones (empathetic vs. direct) and observe outcomes.",
              },
            ].map((tip, idx) => (
              <div key={idx} className="border-l-4 border-blue-600 bg-gray-50 p-4 rounded-lg shadow-sm">
                <h4 className="text-lg font-semibold text-gray-800 mb-1">{tip.title}</h4>
                <p className="text-gray-600 text-sm">{tip.desc}</p>
              </div>
            ))}
          </div>

          {/* Best Practices */}
          <h3 className="text-2xl font-semibold text-blue-700 mb-4">Best Practices</h3>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                title: "Set Clear Goals",
                desc: "Know what success looks like for each conversation before you begin.",
              },
              {
                title: "Review Feedback Regularly",
                desc: "Track progress on the History page to spot improvement trends.",
              },
              {
                title: "Use Custom Scenarios",
                desc: "Tailor simulations to reflect real challenges you’ve faced.",
              },
              {
                title: "Take Breaks",
                desc: "Avoid mental fatigue by pausing between simulations.",
              },
              {
                title: "Practice Consistently",
                desc: "Make practice a habit—small, frequent sessions drive growth.",
              },
            ].map((practice, idx) => (
              <div key={idx} className="border-l-4 border-purple-600 bg-gray-50 p-4 rounded-lg shadow-sm">
                <h4 className="text-lg font-semibold text-gray-800 mb-1">{practice.title}</h4>
                <p className="text-gray-600 text-sm">{practice.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section id="faqs" className="mt-10">
        <div className="bg-white shadow-lg rounded-xl p-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-8">Frequently Asked Questions (FAQs)</h2>
          {Object.entries(faqs).map(([section, questions]) => (
            <div key={section} className="mb-6">
              <h3 className="text-xl font-semibold text-purple-700 mb-4">{section}</h3>
              <div className="space-y-3">
                {questions.map(({ q, a }, idx) => (
                  <Disclosure key={idx}>
                    {({ open }) => (
                      <div className="border border-gray-200 rounded-lg">
                        <DisclosureButton className="w-full flex justify-between items-center px-4 py-3 text-left text-gray-800 font-medium hover:bg-gray-50 transition">
                          <span>{q}</span>
                          <ChevronDownIcon
                            className={`h-5 w-5 transform transition-transform ${open ? 'rotate-180' : ''}`}
                          />
                        </DisclosureButton>
                        <DisclosurePanel className="px-4 pb-4 text-sm text-gray-600">
                          {a}
                        </DisclosurePanel>
                      </div>
                    )}
                  </Disclosure>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="contact-support" className="mt-10">
        <div className="bg-white shadow-xl rounded-xl p-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">Contact Support</h2>
          <p className="mb-4 text-gray-700">
            If you have any questions, need help with the simulator, or want to provide feedback, our support team is here to assist you.
          </p>

          <div className="space-y-4 mb-6">
            <div className="flex items-start gap-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 12h2a2 2 0 002-2V7a2 2 0 00-2-2h-2M8 12H6a2 2 0 01-2-2V7a2 2 0 012-2h2m0 0V5a2 2 0 114 0v2m0 0a2 2 0 110 4v6a2 2 0 11-4 0v-6a2 2 0 110-4z" />
              </svg>
              <p className="text-gray-700">
                <strong>Email Support:</strong> Reach out at <a href="mailto:support@proactr.com" className="text-blue-600 underline">support@proactr.com</a>.
              </p>
            </div>

            <div className="flex items-start gap-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16.72 2a9.954 9.954 0 01-4.75 8.12C8.9 12.18 6.7 14.67 5.18 17.46a2 2 0 002.82 2.82C9.33 21.3 11.82 19.1 13.88 17.03A9.954 9.954 0 0122 7.28a2 2 0 00-5.28-5.28z" />
              </svg>
              <p className="text-gray-700">
                <strong>Contact Form:</strong> Use our <a href="https://proactr.com/contact" className="text-green-600 underline" target="_blank" rel="noopener noreferrer">contact page</a> to submit your questions or feedback.
              </p>
            </div>
          </div>

          <p className="text-gray-700">
            We’re committed to providing the best experience possible and welcome your feedback to help improve the Leadership Simulator.
          </p>
        </div>
      </section>
    </div>
  );
};

export default HelpPage;
